// Engine + transport abstraction. Three page engines sit behind one encode/decode/navReady API:
//   scramjet2  "Standard"       Scramjet 2 (default). Rewrites in this page; the SW only relays.
//   scramjet   "Standard (v1)"  Scramjet 1.1.0. Rewrites in the service worker.
//   uv         "Compatible"     Ultraviolet.
// Labels live in shared/settings-schema.js (ENGINE_CHOICES), the one map every surface uses.
// All engine asset paths, the routing prefixes, the wisp path and the SW name come from the
// build-injected manifest (__RVM) so nothing in the network tab names an engine.
//
// The engine loaders themselves are a lazy chunk (app/engines.js, __RVM.enginesJs) imported in
// boot() after load + idle, together with only the engine new pages open with. The others load
// on first use: a site that needs Standard (v1), or a change in Settings.

import { TRANSPORTS, normalizeEngine, normalizeTransport } from "../shared/settings-schema.js";
import { get, subscribe } from "../shared/settings-store.js";
import { notifyAssetFailure } from "./update.js";
import { initRecovery, recoverProxy } from "./recovery.js";
import { sandboxOrigin, reportTier, signedIn } from "./sbx.js";
import { contentEl, folderEl } from "./dom.js";
import { startTransport, normSteer, liveSteer } from "../shared/ladder.js";

// __RVM.* are replaced at build time by esbuild `define` (see build.mjs).
const M = {
	sjAll: __RVM.sjAll, sjWasm: __RVM.sjWasm, sjSync: __RVM.sjSync, sjPrefix: __RVM.sjPrefix,
	sj2Js: __RVM.sj2Js, sj2Wasm: __RVM.sj2Wasm, sj2Api: __RVM.sj2Api, sj2Inject: __RVM.sj2Inject, sj2Virt: __RVM.sj2Virt, sj2Prefix: __RVM.sj2Prefix,
	uvBundle: __RVM.uvBundle, uvConfig: __RVM.uvConfig, uvPrefix: __RVM.uvPrefix,
	libcurl: __RVM.libcurl, epoxy: __RVM.epoxy, baremuxIndex: __RVM.baremuxIndex, baremuxWorker: __RVM.baremuxWorker,
	serverTransport: __RVM.serverTransport, egress: __RVM.egress,
	wisp: __RVM.wisp, sw: __RVM.sw,
};

// Engine routing prefixes (used by view.js to recognise proxied URLs).
export const PREFIXES = { sj: M.sjPrefix, sj2: M.sj2Prefix, uv: M.uvPrefix };

let E = null; // app/engines.js once loaded
// Sandbox mode (docs/security/PHASE1-PLAN.md, Stage C): the engine bridge (app/engine-bridge.js)
// when this page's proxied sites run on the sandbox origin, else null (legacy: everything below
// runs the engines in this page, exactly as before Phase 1).
let R = null;
export const remote = () => R;
let conn = null;
let currentTransport = "libcurl";
// Created eagerly at module load (pending) so navReady() never returns a transient
// already-resolved promise before boot() runs. It resolves ONLY when the default engine is
// loaded + a transport is up - so a gated navigation never encodes with a null engine
// (which would load the raw URL). On engine-load failure it stays pending
// (navigations wait on the loader) while notifyAssetFailure() drives an update-reload.
let navReadyResolve;
const navReadyPromise = new Promise((r) => { navReadyResolve = r; });

// What the connection dot reads. engine/transport are what is IN USE for new pages; the saved*
// fields are the Settings values; engineWhy/transportAuto say why they differ ("(auto)").
export const status = {
	ready: false, healthy: true,
	engine: "scramjet2", engineWhy: null, savedEngine: "scramjet2",
	transport: "libcurl", savedTransport: "libcurl", transportAuto: null,
};

const savedEngine = () => normalizeEngine(get("engine"));
const savedTransport = () => normalizeTransport(get("transport"));
const hostOf = (u) => { try { return new URL(u).hostname.toLowerCase(); } catch { return ""; } };
export const wispUrl = () => (location.protocol === "https:" ? "wss" : "ws") + "://" + location.host + M.wisp;
// Tunnel trouble this page saw (app/recover.js sets the check once it loads): new pages start on Server.
let tunnelSuspect = () => false;
export function setTunnelSuspect(fn) { if (typeof fn === "function") tunnelSuspect = fn; }

// --- server engine switches (rollback without a release) ----------------------
// The server publishes { sj2: "on"|"off" } in the presence-beacon reply (presence.js re-emits it
// as "rv:engines") and in /__rv/version. "off" makes Standard open pages with Standard (v1); the
// saved setting is never rewritten, so switching it back on restores Scramjet 2 for everyone.
const FLAGS_KEY = "rv:engine-flags";
let flags = (() => {
	try { const f = JSON.parse(localStorage.getItem(FLAGS_KEY) || "{}"); return { sj2: f && f.sj2 === "off" ? "off" : "on" }; } catch { return { sj2: "on" }; }
})();
// Steering for new pages ({ avoid, tunnel }, lib/engine-flags.js): kept in memory only and dropped
// when the server stops refreshing it (ttl), never written into Settings.
let steerState = null;
export function steer() { return liveSteer(steerState, Date.now()); }
function applyEngineFlags(f) {
	if (!f || typeof f !== "object") return;
	const before = JSON.stringify(steer());
	steerState = normSteer(f, Date.now());
	if (R && JSON.stringify(steer()) !== before) R.pushSettings();
	const next = { sj2: f.sj2 === "off" ? "off" : "on" };
	if (next.sj2 === flags.sj2) return;
	flags = next;
	try { localStorage.setItem(FLAGS_KEY, JSON.stringify(flags)); } catch { /* storage off */ }
	refreshStatus();
	if (status.ready && E) E.ensureEngine(status.engine).catch(() => {}); // warm whatever new pages will use
}
if (typeof window !== "undefined") window.addEventListener("rv:engines", (e) => applyEngineFlags(e.detail));

function refreshStatus() {
	if (R) { R.pushSettings(); return; } // S decides what new pages use and reports it
	status.savedEngine = savedEngine();
	status.savedTransport = savedTransport();
	const d = E ? E.defaultEngine() : { engine: status.savedEngine === "scramjet2" && flags.sj2 === "off" ? "scramjet" : status.savedEngine, why: status.savedEngine === "scramjet2" && flags.sj2 === "off" ? "paused" : null };
	status.engine = d.engine;
	status.engineWhy = d.why;
}

function loadScript(src) {
	return new Promise((res, rej) => {
		if ([...document.scripts].some((s) => s.src.endsWith(src))) return res();
		const s = document.createElement("script");
		s.src = src;
		s.onload = () => res();
		s.onerror = () => { try { notifyAssetFailure(); } catch { /* best effort */ } rej(new Error("failed to load " + src)); }; // a 404 here usually means a pruned old build
		document.head.appendChild(s);
	});
}

// Synchronous encode with an engine that is already up (favicons, the health check). Uses the
// tab's engine when given, else the one new pages open with. Returns the input UNCHANGED if no
// engine is ready; callers guard against that (see view.js).
export function encode(url, tab) {
	const out = E && E.encode(url, tab);
	return out == null ? url : out;
}

// The navigation encoder: picks the engine for this tab + URL (site memory, a per-tab fallback,
// the server switch), loads it if needed, records it on the tab (tab.engine / tab.engineWhy) and
// returns the src. Scramjet 2 failing to start falls back to Standard (v1) instead of failing.
export async function encodeFor(url, tab) {
	await navReadyPromise;
	return R ? R.encodeFor(url, tab) : E.encodeFor(url, tab);
}

// Scramjet 2 failed on this tab's page in a way another engine may not (a rewriter error, or no
// controller answered): the next encodeFor() for this site and tab uses Standard (v1), and the
// site is remembered. False when there is nothing to fall back to.
export function engineFallback(tab) { return R ? R.fallback(tab) : !!E && E.engineFallback(tab); }
// The tab menu's "Reload with ..." choice: this tab, this site, until it navigates elsewhere.
export function useEngineForTab(tab, name) { if (R) R.useEngine(tab, name); else if (E) E.useEngineForTab(tab, name); }

export function decode(href) {
	try {
		const u = new URL(href, location.href);
		if (u.origin !== location.origin) return null;
		const p = u.pathname;
		if (M.sj2Prefix && p.startsWith(M.sj2Prefix)) {
			const m = /^[a-z0-9]{1,16}\/[a-z0-9]{1,16}\/(.+)$/.exec(p.slice(M.sj2Prefix.length));
			if (!m) return null;
			const d = decodeURIComponent(m[1]) + (u.hash && !/#/.test(m[1]) ? u.hash : "");
			return /^[a-z]+:\/\//i.test(d) ? d : null;
		}
		if (M.uvPrefix && p.startsWith(M.uvPrefix) && window.__uv$config) {
			const pfx = location.origin + window.__uv$config.prefix;
			if (u.href.indexOf(pfx) === 0) {
				const d = window.__uv$config.decodeUrl(u.href.slice(pfx.length));
				if (typeof d === "string" && /^[a-z]+:\/\//i.test(d)) return d;
			}
			return null;
		}
		if (M.sjPrefix && p.startsWith(M.sjPrefix)) {
			const sj = E && E.sj1();
			const d = sj ? sj.decodeUrl(u.href) : decodeURIComponent(p.slice(M.sjPrefix.length));
			if (typeof d === "string" && /^[a-z]+:\/\//i.test(d)) return d;
		}
	} catch { /* not ours */ }
	return null;
}

// /#open=<value> (a stranded tab handed back, app/main.js): an http(s) address, or an engine path
// that reached the server without our worker (it sends the path; only this page can decode it).
export async function decodeOpen(v) {
	if (typeof v !== "string" || v.length > 8192) return null;
	if (/^https?:\/\//i.test(v)) return v;
	if (v.charAt(0) !== "/" || v.charAt(1) === "/") return null;
	await navReadyPromise;
	if (M.uvPrefix && v.startsWith(M.uvPrefix) && !window.__uv$config) {
		try { if (E) await E.ensureEngine("uv"); else { await loadScript(M.uvBundle); await loadScript(M.uvConfig); } } catch { return null; }
	}
	return decode(location.origin + v);
}

// Sites that needed a different connection this session (e.g. a browser check that only the
// Server connection passes). Navigating to one switches to it; navigating anywhere else puts the
// saved connection back, so an automatic switch never outlives the site that needed it.
const siteTransport = new Map();
// Bumped on every switch. transportFor() reads site memory first (async), and a ladder switch made
// in that gap must win over the stale pick.
let transportSeq = 0;
// The switch the shared worker is still applying. A page must not load before it lands: the worker
// keeps sending requests over the previous connection until then (a hung tunnel holds them ~10 s).
let switching = Promise.resolve();

// opts.auto: an automatic switch for one site (errors.js), shown as "(auto)" until a navigation
// to a site that does not need it puts the saved transport back.
export async function setTransport(name, opts) {
	name = normalizeTransport(name);
	if (R) return R.setTransport(name, opts);
	transportSeq++;
	currentTransport = name;
	status.transport = name;
	status.savedTransport = savedTransport();
	status.transportAuto = opts && opts.auto && name !== status.savedTransport ? { host: opts.host || "", since: Date.now() } : null;
	// A switch is remembered for the site only once it actually delivered a page (noteWay).
	if (opts && opts.auto && opts.host && opts.keep) siteTransport.set(opts.host, name);
	if (!conn) return;
	// "server" egresses through our own curl-backed route (for Cloudflare-challenge
	// hosts the wasm transports can't reach). It ignores wisp entirely.
	if (name === "server" && M.serverTransport) {
		await track(conn.setTransport(M.serverTransport, [{ egress: M.egress }]));
		return;
	}
	const t = TRANSPORTS[name] || TRANSPORTS.libcurl;
	const path = name === "epoxy" ? M.epoxy : M.libcurl;
	const opt = {};
	opt[t.key] = wispUrl();
	await track(conn.setTransport(path, [opt]));
}
function track(p) { switching = p.then(() => {}, () => {}); return p; }

// url "" (the start page, a Re:Vault page) means "no site": the saved connection.
// The start rung's connection (shared/ladder.js startTransport): this session's switch for the
// site, else what the site memory says worked, else Settings; steered off a connection the server
// says to avoid, and onto Server while our tunnel is down.
export async function transportFor(url) {
	if (R) return R.transportFor(url);
	if (!conn) return;
	const host = url ? hostOf(url) : "";
	const saved = savedTransport();
	const seq = transportSeq;
	const mem = host && E ? await E.rememberedTransport(host) : "";
	if (seq !== transportSeq) { await switching; return; }
	const pick = startTransport(saved, { session: siteTransport.get(host) || "", memory: mem ? { t: mem } : null, steer: url ? steer() : null, suspect: !!url && tunnelSuspect(), serverOk: !!M.serverTransport });
	const want = pick.transport;
	if (want === currentTransport && (want !== saved) === !!status.transportAuto) { await switching; return; }
	try { await setTransport(want, want !== saved ? { auto: true, host } : null); } catch { /* reconnect() handles a dead transport */ }
}

// The recovery ladder (app/errors.js): this tab's next attempt uses another engine.
export function retryOn(tab, name) { return R ? R.retryOn(tab, name) : !!E && E.retryOn(tab, name); }
export function availableEngines() { return E ? E.availableEngines() : flags.sj2 === "off" ? ["scramjet", "uv"] : ["scramjet2", "scramjet", "uv"]; }
// How a page on the tab's engine and connection went: "rescued" (a later attempt loaded it),
// "default" (the first attempt did) or "failed". Rescues are remembered per site (on this device
// only) and a working switch is kept for the rest of the session.
export function noteWay(tab, outcome) {
	if (!tab || !tab.url || tab.internal) return;
	const host = hostOf(tab.url);
	const way = { e: tab.engine || status.engine, t: currentTransport };
	if (R) { R.noteWay(tab, way, outcome); return; }
	if (outcome === "rescued") { if (way.t !== savedTransport()) siteTransport.set(host, way.t); else siteTransport.delete(host); }
	else if (outcome === "default" && way.t === savedTransport()) siteTransport.delete(host);
	if (E) E.noteWay(host, way, outcome, savedTransport());
}
export const egressPrefix = () => M.egress || "";

// The page watchdog's grace period (app/errors.js; logic in app/engines.js).
export const watchGrace = (t, f) => (E ? E.watchGrace(t, f) : 0);

export async function setEngine() {
	refreshStatus();
	if (status.ready && E) { try { await E.ensureEngine(status.engine); } catch { refreshStatus(); } }
}

// The connection dot for the active tab: { title, auto } from the shared label map.
export function connectionTitle(tab) { return E ? E.dotInfo(tab) : R ? R.dot(tab, status) : { title: "", auto: false }; }

export function engine() { return status.engine; }
export function transport() { return currentTransport; }
export function navReady() { return navReadyPromise; }

export async function reconnect() {
	if (R) { status.healthy = await R.reconnect(); return status.healthy; }
	try { await setTransport(currentTransport, status.transportAuto ? { auto: true, host: status.transportAuto.host } : null); status.healthy = true; return true; }
	catch { status.healthy = false; return false; }
}

// Resolve once the page has loaded AND the main thread is idle, so the heavy engine
// bundles never delay first paint / LCP / interactivity.
function afterLoadIdle() {
	return new Promise((res) => {
		const idle = () => (window.requestIdleCallback ? requestIdleCallback(() => res(), { timeout: 1500 }) : setTimeout(res, 120));
		if (document.readyState === "complete") idle();
		else addEventListener("load", idle, { once: true });
	});
}

// The engine API other app modules share (recovery.js health check, engines.js hooks). It is NOT a
// window global: every proxied page is same-origin with this window and could drive it. Tests get
// it as window.__revault only when they set window.__RV_TEST_HOOKS__ before the app starts
// (tools/engines-check.mjs does, with evaluateOnNewDocument); production never does.
const api = { encode, decode, navReady, status, setEngine, setTransport, reconnect };

// Sandbox mode: the engines run in the engine host on the sandbox origin (app/engine-bridge.js
// does the handshake and mirrors S's status into `status`). True when this page is in sandbox mode;
// false (the legacy boot follows) when sbx is off for it, or S did not answer in 4 s.
// hooks: where S's reports go (app/main.js wires them to view.js, errors.js and navigation).
async function bootSandbox(hooks) {
	const origin = hooks ? await sandboxOrigin() : "";
	if (!origin) return false;
	try {
		R = await (await import(__RVM.bridgeJs)).start({
			origin, container: contentEl, before: folderEl, get, subscribe, flags: () => flags, steer, suspect: () => tunnelSuspect(), hooks, status, sw: M.sw,
			ready: () => navReadyResolve(true), transport: (t) => { currentTransport = t; }, assetFailure: notifyAssetFailure, recover: recoverProxy, signedIn,
		});
	} catch { R = null; }
	reportTier(R ? "A" : "failed");
	return !!R;
}

export function boot(hooks) {
	refreshStatus();
	// Self-healing watchdog: if the engines never come up (or boot throws below),
	// converge onto a clean state instead of leaving the user stuck (no Ctrl+Shift+R).
	initRecovery(navReadyPromise, { api });
	// The engine bundles are NOT in index.html - they're loaded here, after load+idle, off the
	// critical path. navReady resolves only after this fully succeeds.
	(async () => {
		try {
			await afterLoadIdle();
			if (await bootSandbox(hooks)) return;
			const mod = await import(__RVM.enginesJs);
			mod.init({ M, status, flags: () => flags, savedEngine, keepHistory: () => !get("incognito") && get("keepHistory") !== false, loadScript, refresh: refreshStatus, reconnect, api });
			E = mod;
			await loadScript(M.baremuxIndex);
			conn = new BareMux.BareMuxConnection(M.baremuxWorker);
			await E.ensureSj1Db(); // before the worker exists: see engines.js
			if (navigator.serviceWorker) {
				// A failed/404'd SW (e.g. registered against a pruned old build) must not
				// hang boot - degrade gracefully so the app still loads.
				try {
					await navigator.serviceWorker.register(M.sw, { scope: "/" });
					// A legacy load clears the worker's sandbox-mode latch (Phase 1, engines.js).
					await E.sbxUnlatch(await navigator.serviceWorker.ready);
				} catch {
					try { notifyAssetFailure(); } catch { /* best effort */ }
				}
			}
			await setTransport(savedTransport());
			refreshStatus();
			// Decide the fallback from the engine that was ASKED for: a failing Scramjet 2 start
			// marks itself down (engines.js markSj2Down -> refresh()), so status.engine already
			// reads "scramjet" by the time the catch runs.
			const want = status.engine;
			try { await E.ensureEngine(want); }
			catch (e) {
				if (want !== "scramjet2") throw e;
				// Scramjet 2 could not start here (browser, an older worker, or a timeout): new
				// pages open with Standard (v1) for this page's lifetime, shown as "(auto)".
				refreshStatus();
				await E.ensureEngine(status.engine === "scramjet2" ? "scramjet" : status.engine);
			}
			status.ready = true;
			navReadyResolve(true); // engines up - gated navigations may now encode
		} catch {
			// Engine assets failed to load. Deliberately do NOT resolve navReady - gated
			// navigations keep waiting on the loader rather than loading raw URLs;
			// notifyAssetFailure() triggers an auto-update reload onto a good build.
			status.healthy = false;
			try { notifyAssetFailure(); } catch { /* best effort */ }
			try { recoverProxy("boot-fail"); } catch { /* best effort */ } // self-heal: reload onto a clean build
		}
	})();

	// app/engines.js init() adds health5xx (recovery.js), clearSiteCookies (Settings) and, in
	// tests only, _sj2.
	if (window.__RV_TEST_HOOKS__ === true) window.__revault = api;
	return navReadyPromise;
}
